/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useObservable } from '@squiz/dxp-console-support';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { EntityTenantSettingsSchemaList } from '../../Entities/TenantSettingsSchemaList/TenantSettingsSchemaList.entity';
import { TenantSettingsServiceGetSchemaListDto } from '../../TenantSettings.service.dto';
import { getTenantSettingsService } from '../../Utilities/GetTenantSettingsService/GetTenantSettingsService';

/**
 * The result expected of the useSchemaList hook.
 */
export type TenantSettingsSchemaListResult = [EntityTenantSettingsSchemaList | null, JsonObject | null];

/**
 * React hook to get a list of available schemas for the tenant settings service.
 */
export function useSchemaList(dto: TenantSettingsServiceGetSchemaListDto): TenantSettingsSchemaListResult {
  const tenantSettingsService = getTenantSettingsService();

  const schemaList$ = useMemo((): Observable<EntityTenantSettingsSchemaList | null> => {
    return tenantSettingsService.getSchemaList(dto);
  }, [dto, tenantSettingsService]);

  const [schemaList, error] = useObservable(schemaList$, [tenantSettingsService]);

  return useMemo((): TenantSettingsSchemaListResult => {
    return [schemaList as EntityTenantSettingsSchemaList, error as JsonObject];
  }, [error, schemaList]);
}
